import React, { useState } from 'react';
import { useSiteContent } from '../../context/SiteContentContext';
import './AnnouncementBar.css';

// Site-wide announcement set from Admin → Content. Dismissing hides that exact
// message for the rest of the browser session; a new message shows again.
const keyFor = (text) => 'spaczo_ann_dismissed_' + Array.from(text).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);

// "ends in 2 days" / "ends today"
function endsIn(iso) {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '';
  const days = Math.floor(ms / 86400e3);
  if (days >= 2) return `ends in ${days} days`;
  if (days === 1) return 'ends tomorrow';
  const hours = Math.max(1, Math.round(ms / 3600e3));
  return `ends in ${hours} hour${hours === 1 ? '' : 's'}`;
}

// What the offer gives, in a few words.
function offerSummary(o) {
  const parts = [];
  if (o.percent) parts.push(`${o.percent}% off plans`);
  else if (Object.keys(o.plans || {}).length) parts.push('special plan prices');
  if (o.extraTrialDays) parts.push(`${o.extraTrialDays} extra free-trial days`);
  if (o.listingPercent) parts.push(`${o.listingPercent}% off listing fees`);
  return parts.join(' · ');
}

function OfferBar({ offer, onClose }) {
  const text = offer.bannerText || `${offer.name}: ${offerSummary(offer)}`;
  return (
    <div className="ann-bar ann-offer" role="status">
      <span className="ann-text">🎉 {text}{offer.audience === 'new_owners' ? ' (new owners)' : ''} <b className="ann-ends">· {endsIn(offer.endsAt)}</b></span>
      <a className="ann-link" href="/?tab=mylistings&section=plan">See offer →</a>
      <button className="ann-close" onClick={onClose} aria-label="Dismiss offer">✕</button>
    </div>
  );
}

export default function AnnouncementBar() {
  const { announcement, offers } = useSiteContent();
  const [closedKey, setClosedKey] = useState(null);

  // A live offer takes the bar's place while it runs.
  const offer = (offers || []).find(o => o.showBanner);
  if (offer) {
    const okey = 'spaczo_offer_dismissed_' + offer.id;
    let gone = false;
    try { gone = sessionStorage.getItem(okey) === '1'; } catch {}
    if (!gone && closedKey !== okey) {
      return <OfferBar offer={offer} onClose={() => { try { sessionStorage.setItem(okey, '1'); } catch {} setClosedKey(okey); }} />;
    }
  }

  const text = announcement?.text?.trim();
  if (!announcement?.enabled || !text) return null;

  const key = keyFor(text);
  let storedClosed = false;
  try { storedClosed = sessionStorage.getItem(key) === '1'; } catch {}
  if (storedClosed || closedKey === key) return null;

  const close = () => {
    try { sessionStorage.setItem(key, '1'); } catch {}
    setClosedKey(key);
  };

  const link = announcement.link;
  const external = link && /^https:\/\//.test(link);

  return (
    <div className={`ann-bar ann-${announcement.tone || 'info'}`} role="status">
      <span className="ann-text">{text}</span>
      {link && (
        <a className="ann-link" href={link} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          Learn more →
        </a>
      )}
      <button className="ann-close" onClick={close} aria-label="Dismiss announcement">✕</button>
    </div>
  );
}
